// src/components/SolicitudMudanzaReviewModal.js
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FaCalendarAlt,
    FaClock,
    FaEnvelope,
    FaFileAlt,
    FaHammer,
    FaMapMarkerAlt,
    FaPhone,
    FaTimes,
    FaUserCircle,
} from 'react-icons/fa';
import './SolicitudMudanzaReviewModal.css';

const STATUS_OPTIONS = [
    { key: 'pendiente', label: 'Pendiente', tone: 'warning' },
    { key: 'revision', label: 'En revisión', tone: 'info' },
    { key: 'aprobado', label: 'Aprobado', tone: 'success' },
    { key: 'rechazado', label: 'Rechazado', tone: 'danger' },
];

const STATUS_META_BY_KEY = STATUS_OPTIONS.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
}, {});

const REMODEL_TRABAJO_LABELS = {
    pintura: 'Pintura',
    cambio_pisos: 'Cambio de pisos',
    ventanas_puertas: 'Ventanas / Puertas',
    impermeabilizacion: 'Impermeabilización',
    remodelacion_cocina_bano: 'Remodelación cocina / baño',
    modificacion_fachada: 'Modificación de fachada',
    otros: 'Otros',
};

const REMODEL_RESPONSABLE_LABELS = {
    propietario: 'Propietario',
    contratista: 'Contratista',
    ambos: 'Propietario y contratista',
};

const REMODEL_RUIDO_LABELS = {
    no: 'No',
    poco: 'Poco',
    moderado: 'Moderado',
    alto: 'Alto',
};

const formatDateTime = (timestamp) => {
    if (!timestamp) {
        return 'Pendiente';
    }
    if (timestamp.toDate) {
        try {
            return timestamp.toDate().toLocaleString('es-MX');
        } catch (error) {
            return 'Fecha inválida';
        }
    }
    return 'Fecha inválida';
};

const formatDateOnly = (timestamp) => {
    if (!timestamp) {
        return 'Sin fecha definida';
    }
    if (timestamp.toDate) {
        try {
            return timestamp.toDate().toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (error) {
            return 'Fecha inválida';
        }
    }
    return 'Fecha inválida';
};

const SolicitudMudanzaReviewModal = ({ request, onClose, onStatusChange, updating, errorMessage }) => {
    // DEBUG: Log de datos de solicitud para debugging
    React.useEffect(() => {
        if (request) {
            console.log('[SolicitudMudanzaReviewModal] Solicitud recibida:', {
                tipoSolicitud: request.tipoSolicitud,
                titulo: request.titulo,
                horaProgramada: request.horaProgramada,
                horarioLlegada: request.horarioLlegada,
                horaInicio: request.horaInicio,
                fechaProgramada: request.fechaProgramada,
                fechaInicioTrabajo: request.fechaInicioTrabajo,
                estado: request.estado,
                allKeys: Object.keys(request),
            });
        }
    }, [request]);

    const statusKey = (request?.estado || 'pendiente').toLowerCase();
    const currentStatus = STATUS_META_BY_KEY[statusKey] || STATUS_META_BY_KEY.pendiente;
    const solicitudType = (request?.tipoSolicitud || '').toLowerCase();
    const isConstruccion = solicitudType === 'construccion';
    const isRemodelacion = solicitudType === 'remodelacion';

    const attachments = useMemo(() => {
        if (!request) {
            return [];
        }
        if (isConstruccion) {
            return ([
                request.docLicenciaUrl && {
                    key: 'licencia',
                    label: 'Licencia municipal',
                    url: request.docLicenciaUrl,
                },
                request.docPlanosUrl && {
                    key: 'planos',
                    label: 'Planos arquitectónicos',
                    url: request.docPlanosUrl,
                },
                request.docIdentificacionUrl && {
                    key: 'identificacion',
                    label: 'Identificación del propietario',
                    url: request.docIdentificacionUrl,
                },
                request.docPredialUrl && {
                    key: 'predial',
                    label: 'Recibo de predial',
                    url: request.docPredialUrl,
                },
                request.docCronogramaUrl && {
                    key: 'cronograma',
                    label: 'Cronograma de obra',
                    url: request.docCronogramaUrl,
                },
            ].filter(Boolean));
        }
        if (isRemodelacion) {
            return ([
                request?.docIdentificacionUrl && {
                    key: 'identificacion',
                    label: 'Identificación oficial',
                    url: request.docIdentificacionUrl,
                },
                request?.docFotografiasUrl && {
                    key: 'fotografias',
                    label: 'Fotografías del área',
                    url: request.docFotografiasUrl,
                },
                request?.docBocetoUrl && {
                    key: 'boceto',
                    label: 'Boceto o render de la modificación',
                    url: request.docBocetoUrl,
                },
            ].filter(Boolean));
        }
        return ([
            request?.docIdentificacionUrl && {
                key: 'identificacion',
                label: 'Identificación oficial',
                url: request.docIdentificacionUrl,
            },
            request?.docComprobanteUrl && {
                key: 'comprobante',
                label: 'Comprobante de domicilio',
                url: request.docComprobanteUrl,
            },
            request?.docInventarioUrl && {
                key: 'inventario',
                label: 'Inventario de mudanza',
                url: request.docInventarioUrl,
            },
        ].filter(Boolean));
    }, [isConstruccion, isRemodelacion, request]);

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget && !updating) {
            onClose();
        }
    };

    return (
        <div className="mudanza-review-overlay" role="presentation" onClick={handleOverlayClick}>
            <div className="mudanza-review-modal" role="dialog" aria-modal="true">
                <header className="mudanza-review-header">
                    <div className="mudanza-review-header-meta">
                        <div className="mudanza-review-avatar">
                            <FaUserCircle />
                        </div>
                        <div>
                            <h2>{request?.titulo || request?.uploaderNombre || 'Solicitud de mudanza'}</h2>
                            <span className={`mudanza-review-status ${currentStatus.tone}`}>
                                {currentStatus.label}
                            </span>
                        </div>
                    </div>
                    <button type="button" className="mudanza-review-close" onClick={onClose} disabled={updating}>
                        <FaTimes />
                    </button>
                </header>

                <section className="mudanza-review-body">
                    {isConstruccion ? (
                        <>
                            <div className="mudanza-review-grid">
                                <div>
                                    <span className="mudanza-review-label"><FaUserCircle /> Propietario</span>
                                    <p>{request?.nombreCompleto || request?.uploaderNombre || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaMapMarkerAlt /> Lote / Casa</span>
                                    <p>{request?.lote || request?.houseLabel || 'Sin asignar'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaPhone /> Teléfono del propietario</span>
                                    <p>{request?.telefonoPrincipal || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaEnvelope /> Correo electrónico</span>
                                    <p>{request?.email || 'Sin registro'}</p>
                                </div>
                            </div>

                            <div className="mudanza-review-section">
                                <h3>Datos del Constructor</h3>
                                <div className="mudanza-review-grid">
                                    <div>
                                        <span className="mudanza-review-label">Empresa constructora</span>
                                        <p>{request?.empresaConstructora || 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Encargado de obra</span>
                                        <p>{request?.encargadoObra || 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Teléfono del encargado</span>
                                        <p>{request?.telefonoEncargado || 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">RFC</span>
                                        <p>{request?.rfcConstructora || '—'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mudanza-review-section">
                                <h3>Detalles de la Obra</h3>
                                <div className="mudanza-review-grid">
                                    <div>
                                        <span className="mudanza-review-label">Tipo de construcción</span>
                                        <p>{request?.tipoConstruccion || 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Metros cuadrados</span>
                                        <p>{request?.metrosCuadrados != null ? `${request.metrosCuadrados}` : 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Número de niveles</span>
                                        <p>{request?.niveles != null ? `${request.niveles}` : 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label"><FaCalendarAlt /> Periodo estimado</span>
                                        <p>
                                            {request?.fechaInicioObra || request?.fechaTerminoObra
                                                ? `${formatDateOnly(request?.fechaInicioObra)} - ${formatDateOnly(request?.fechaTerminoObra)}`
                                                : 'Sin fechas definidas'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label"><FaClock /> Horario de llegada</span>
                                        <p>{request?.horarioLlegada ? `${request.horarioLlegada} hrs` : 'Sin horario asignado'}</p>
                                    </div>
                                </div>
                            </div>

                            {request?.descripcionConstruccion && (
                                <div className="mudanza-review-section">
                                    <h3>Descripción de la obra</h3>
                                    <p>{request.descripcionConstruccion}</p>
                                </div>
                            )}
                        </>
                    ) : isRemodelacion ? (
                        <>
                            <div className="mudanza-review-grid">
                                <div>
                                    <span className="mudanza-review-label"><FaUserCircle /> Propietario</span>
                                    <p>{request?.nombreCompleto || request?.uploaderNombre || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaMapMarkerAlt /> Lote / Casa</span>
                                    <p>{request?.lote || request?.houseLabel || 'Sin asignar'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaPhone /> Teléfono principal</span>
                                    <p>{request?.telefonoPrincipal || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaEnvelope /> Correo electrónico</span>
                                    <p>{request?.email || 'Sin registro'}</p>
                                </div>
                            </div>

                            <div className="mudanza-review-section">
                                <h3>Detalles de la remodelación</h3>
                                <div className="mudanza-review-grid">
                                    <div>
                                        <span className="mudanza-review-label"><FaHammer /> Tipo de trabajo</span>
                                        <p>{REMODEL_TRABAJO_LABELS[request?.tipoTrabajo] || 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Responsable de los trabajos</span>
                                        <p>{REMODEL_RESPONSABLE_LABELS[request?.responsableTrabajo] || 'Sin registro'}</p>
                                    </div>
                                    {request?.nombreContratista && (
                                        <div>
                                            <span className="mudanza-review-label">Contratista</span>
                                            <p>{request.nombreContratista}</p>
                                        </div>
                                    )}
                                    <div>
                                        <span className="mudanza-review-label"><FaCalendarAlt /> Fecha de inicio</span>
                                        <p>{formatDateOnly(request?.fechaInicioTrabajo)}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label"><FaClock /> Duración estimada</span>
                                        <p>{request?.duracionDias != null ? `${request.duracionDias} días` : 'Sin registro'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label"><FaClock /> Horario de llegada</span>
                                        <p>{request?.horarioLlegada ? `${request.horarioLlegada} hrs` : 'Sin horario asignado'}</p>
                                    </div>
                                    <div>
                                        <span className="mudanza-review-label">Generará ruido</span>
                                        <p>{REMODEL_RUIDO_LABELS[request?.generaraRuido] || 'Sin registro'}</p>
                                    </div>
                                </div>
                            </div>

                            {request?.descripcionTrabajos && (
                                <div className="mudanza-review-section">
                                    <h3>Descripción de los trabajos</h3>
                                    <p>{request.descripcionTrabajos}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="mudanza-review-grid">
                                <div>
                                    <span className="mudanza-review-label"><FaMapMarkerAlt /> Lote / Casa</span>
                                    <p>{request?.lote || request?.houseLabel || 'Sin asignar'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaPhone /> Teléfono principal</span>
                                    <p>{request?.telefonoPrincipal || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaEnvelope /> Correo electrónico</span>
                                    <p>{request?.email || 'Sin registro'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaCalendarAlt /> Fecha programada</span>
                                    <p>{formatDateOnly(request?.fechaProgramada)}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label"><FaClock /> Horario estimado</span>
                                    <p>{request?.horaProgramada ? `${request.horaProgramada} hrs` : 'Sin horario asignado'}</p>
                                </div>
                                <div>
                                    <span className="mudanza-review-label">Tipo de mudanza</span>
                                    <p>{request?.tipoMudanza || request?.tipoSolicitud || 'Entrada'}</p>
                                </div>
                            </div>

                            {request?.empresaMudanza && (
                                <div className="mudanza-review-section">
                                    <h3>Empresa de mudanza</h3>
                                    <p>{request.empresaMudanza}</p>
                                </div>
                            )}

                            {request?.placasVehiculos && (
                                <div className="mudanza-review-section">
                                    <h3>Placas registradas</h3>
                                    <p>{request.placasVehiculos}</p>
                                </div>
                            )}

                            {request?.observaciones && (
                                <div className="mudanza-review-section">
                                    <h3>Observaciones del residente</h3>
                                    <p>{request.observaciones}</p>
                                </div>
                            )}
                        </>
                    )}

                    <div className="mudanza-review-section">
                        <h3>Archivos adjuntos</h3>
                        {attachments.length === 0 ? (
                            <p className="mudanza-review-empty">No hay archivos disponibles.</p>
                        ) : (
                            <ul className="mudanza-review-files">
                                {attachments.map((item) => (
                                    <li key={item.key}>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                                            <FaFileAlt />
                                            <span>{item.label}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="mudanza-review-meta">
                        <span>Enviada el {formatDateTime(request?.createdAt)}</span>
                        {request?.ultimaActualizacion && (
                            <span>Última actualización {formatDateTime(request.ultimaActualizacion)}</span>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="mudanza-review-error">{errorMessage}</div>
                    )}
                </section>

                <footer className="mudanza-review-footer">
                    <div className="mudanza-review-actions">
                        {STATUS_OPTIONS.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                className={`mudanza-review-action ${option.tone} ${option.key === currentStatus.key ? 'active' : ''}`}
                                onClick={() => onStatusChange(option.key)}
                                disabled={updating || option.key === currentStatus.key}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <button type="button" className="mudanza-review-secondary" onClick={onClose} disabled={updating}>
                        Cerrar
                    </button>
                </footer>
            </div>
        </div>
    );
};

SolicitudMudanzaReviewModal.propTypes = {
    request: PropTypes.shape({
        id: PropTypes.string,
        estado: PropTypes.string,
        uploaderNombre: PropTypes.string,
        titulo: PropTypes.string,
        nombreCompleto: PropTypes.string,
        lote: PropTypes.string,
        houseLabel: PropTypes.string,
        telefonoPrincipal: PropTypes.string,
        email: PropTypes.string,
        fechaProgramada: PropTypes.shape({ toDate: PropTypes.func }),
        horaProgramada: PropTypes.string,
        tipoMudanza: PropTypes.string,
        tipoSolicitud: PropTypes.string,
        empresaMudanza: PropTypes.string,
        placasVehiculos: PropTypes.string,
        observaciones: PropTypes.string,
        empresaConstructora: PropTypes.string,
        encargadoObra: PropTypes.string,
        telefonoEncargado: PropTypes.string,
        rfcConstructora: PropTypes.string,
        tipoConstruccion: PropTypes.string,
        descripcionConstruccion: PropTypes.string,
        metrosCuadrados: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        niveles: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        fechaInicioObra: PropTypes.shape({ toDate: PropTypes.func }),
        fechaTerminoObra: PropTypes.shape({ toDate: PropTypes.func }),
        docIdentificacionUrl: PropTypes.string,
        docComprobanteUrl: PropTypes.string,
        docInventarioUrl: PropTypes.string,
        docLicenciaUrl: PropTypes.string,
        docPlanosUrl: PropTypes.string,
        docPredialUrl: PropTypes.string,
        docCronogramaUrl: PropTypes.string,
        responsableTrabajo: PropTypes.string,
        nombreContratista: PropTypes.string,
        tipoTrabajo: PropTypes.string,
        descripcionTrabajos: PropTypes.string,
        fechaInicioTrabajo: PropTypes.shape({ toDate: PropTypes.func }),
        duracionDias: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        generaraRuido: PropTypes.string,
        docFotografiasUrl: PropTypes.string,
        docBocetoUrl: PropTypes.string,
        createdAt: PropTypes.shape({ toDate: PropTypes.func }),
        ultimaActualizacion: PropTypes.shape({ toDate: PropTypes.func }),
    }),
    onClose: PropTypes.func.isRequired,
    onStatusChange: PropTypes.func.isRequired,
    updating: PropTypes.bool,
    errorMessage: PropTypes.string,
};

SolicitudMudanzaReviewModal.defaultProps = {
    request: null,
    updating: false,
    errorMessage: '',
};

export default SolicitudMudanzaReviewModal;
